import { apiClient } from './secureApiClient.js';
import { logger } from '../../utils/logger';

/**
 * Validates a single match from the matches array
 * @param {Object} match - Match object with t1p1, t1p2, t2p1, t2p2, score1, score2, matchTime
 * @param {number} index - Index of the match for error messages
 * @returns {Object} Validation result with isValid, error, and data
 */
const validateSingleMatch = (match, index) => {
  const { t1p1, t1p2, t2p1, t2p2, score1, score2 } = match;
  const matchLabel = `Match ${index + 1}`;

  // Check for 4 unique players
  const allPlayers = [t1p1, t1p2, t2p1, t2p2].filter(Boolean);
  if (new Set(allPlayers).size !== 4) {
    return {
      isValid: false,
      error: `${matchLabel}: You must select 4 unique players.`,
    };
  }

  // Validate scores are numbers
  const parsedScore1 = parseInt(score1);
  const parsedScore2 = parseInt(score2);

  if (isNaN(parsedScore1) || isNaN(parsedScore2)) {
    return {
      isValid: false,
      error: `${matchLabel}: Please enter valid scores.`,
    };
  }

  // Validate game rules (one team must win with exactly 10)
  const isTeam1Win = parsedScore1 === 10 && parsedScore2 < 10;
  const isTeam2Win = parsedScore2 === 10 && parsedScore1 < 10;

  if (!isTeam1Win && !isTeam2Win) {
    return {
      isValid: false,
      error: `${matchLabel}: Invalid Score. One team must win with exactly 10 goals. (e.g., 10-8, 10-0)`,
    };
  }

  return {
    isValid: true,
    error: null,
    data: {
      players: { t1p1, t1p2, t2p1, t2p2 },
      scores: { score1: parsedScore1, score2: parsedScore2 },
    },
  };
};

/**
 * Submits multiple matches using secure API
 * @param {Event} e - Form submission event
 * @param {Array} matchesData - Array of match objects from the modal
 * @param {Object} userProfile - Current user profile
 * @param {Array} currentMatches - Current matches array in state
 * @param {Function} setMatches - State setter for matches
 * @param {Function} setShowMatchModal - State setter for match modal
 * @param {Function} setFormError - State setter for form errors
 * @param {Array} players - Current players array for rating calculations
 * @param {Function} setPlayers - State setter for players (for rating updates)
 * @returns {Promise<void>}
 */
export const handleSubmitMultipleMatches = async (
  e,
  matchesData,
  userProfile,
  currentMatches,
  setMatches,
  setShowMatchModal,
  setFormError,
  players = [],
  setPlayers = null
) => {
  e.preventDefault();
  setFormError('');

  logger.debug('Submitting matches:', matchesData.length);

  // Validate all matches
  const validatedMatches = [];
  for (let i = 0; i < matchesData.length; i++) {
    const validation = validateSingleMatch(matchesData[i], i);
    if (!validation.isValid) {
      logger.debug('Match validation failed:', validation.error);
      setFormError(validation.error);
      throw new Error(validation.error);
    }

    const { t1p1, t1p2, t2p1, t2p2 } = validation.data.players;
    const { score1, score2 } = validation.data.scores;
    const matchTime = matchesData[i].matchTime;

    // Convert matchTime from local browser time to UTC ISO string
    let timestamp;
    if (matchTime) {
      timestamp = new Date(matchTime).toISOString();
    } else {
      timestamp = new Date().toISOString();
    }

    validatedMatches.push({
      team1Players: [t1p1, t1p2],
      team2Players: [t2p1, t2p2],
      team1Score: score1,
      team2Score: score2,
      timestamp: timestamp,
    });
  }

  logger.debug('All matches validated:', validatedMatches.length);

  try {
    // Use batch API for all submissions (works for single or multiple)
    const result = await apiClient.createMatches(validatedMatches);

    logger.debug('Matches submitted successfully via API:', result.matches?.length);

    // Update local state with new matches (newest first)
    const newMatches = result.matches || [];
    setMatches([...newMatches.reverse(), ...currentMatches]);

    // Update players with new ratings if available
    if (result.updatedPlayers && setPlayers) {
      const updatedPlayersMap = new Map(
        result.updatedPlayers.map(p => [p.id, p])
      );

      const allPlayersUpdated = players.map(
        player => updatedPlayersMap.get(player.id) || player
      );

      setPlayers(allPlayersUpdated);
      logger.debug('Player ratings updated from API response');
    }

    if (setShowMatchModal) setShowMatchModal(false);
  } catch (error) {
    logger.error('Error submitting matches:', error);
    setFormError(error.message || 'Failed to submit matches');
    throw error;
  }
};

/**
 * Verifies a match using secure API (admin only)
 * @param {string} matchId - ID of the match to verify
 * @param {Object} userProfile - Current user profile
 * @param {Array} matches - Current matches array
 * @param {Function} setMatches - State setter for matches
 * @param {Array} players - Current players array for rating calculations
 * @param {Function} setPlayers - State setter for players (for rating updates)
 * @returns {Promise<void>}
 */
export const handleVerifyMatch = async (
  matchId,
  userProfile,
  matches,
  setMatches
) => {
  if (!userProfile?.isAdmin) return;

  try {
    const verifiedMatch = await apiClient.verifyMatch(matchId, { action: 'verify' });

    logger.debug('Match verified successfully via API:', matchId);

    // Update local state
    setMatches(matches.map(m => (m.id === matchId ? verifiedMatch : m)));

    logger.debug('Match verification completed:', matchId);
  } catch (error) {
    logger.error('Error verifying match via API:', error);
  }
};

/**
 * Loads all matches using secure API
 * @returns {Promise<Array>} - Array of matches
 */
export const loadMatches = async () => {
  try {
    logger.debug('Loading matches via secure API...');
    const matches = await apiClient.getMatches();

    logger.debug('Matches loaded successfully via API:', matches.length);
    return matches;
  } catch (error) {
    logger.error('Error loading matches via API:', error);
    return [];
  }
};
